import axios from "axios";

import type {
	Block,
	BlockDocumentTemplatePart,
	BlockKnowledge,
	CreateBlockDocumentTemplatePartBody,
	SetBlockDocumentTemplateBody,
} from "@virtual-brain-lib/types";

import env from "../environments";
import { queryOptions } from "../query-client/queryOption";

export async function createBlock({
	applicationId,
	index,
	blockName,
}: {
	applicationId: string;
	index: number;
	blockName: string;
}) {
	const response = await axios.post<{ block: Block }>(`${env.apiUrl}/blocks`, {
		applicationId,
		index,
		title: blockName,
	});

	return response.data.block;
}

export async function updateBlock({ blockId, block }: { blockId: string; block: Partial<Block> }) {
	// Remove knowledge property to avoid EntityPropertyNotFoundError
	const { knowledges: _, ...blockWithoutKnowledge } = block;

	const response = await axios.put<{
		block: Block;
	}>(`${env.apiUrl}/blocks/${blockId}`, { block: blockWithoutKnowledge });

	return response.data.block;
}

export async function addBlockKnowledge({
	blockId,
	knowledgeId,
	type,
}: {
	blockId: string;
	knowledgeId: string;
	type: "brain" | "step" | "block";
}) {
	const response = await axios.post<{
		knowledge: BlockKnowledge;
	}>(`${env.apiUrl}/blocks/${blockId}/blocksKnowledge/${knowledgeId}`, { type });

	return response.data.knowledge;
}

export async function deleteBlockKnowledge({
	blockId,
	knowledgeId,
	type,
}: {
	blockId: string;
	knowledgeId: string;
	type: "brain" | "step" | "block";
}) {
	await axios.delete(`${env.apiUrl}/blocks/${blockId}/blocksKnowledge/${knowledgeId}`, {
		data: { type },
	});
}

export async function attachBlockDocumentTemplatePart({
	blockId,
	documentTemplatePartId,
	strict,
}: {
	blockId: string;
	documentTemplatePartId: string;
	strict?: boolean;
}) {
	const body: CreateBlockDocumentTemplatePartBody = { documentTemplatePartId, strict };
	const response = await axios.post<BlockDocumentTemplatePart>(
		`${env.apiUrl}/blocks/${blockId}/document-template-parts`,
		body,
	);

	return response.data;
}

export async function detachBlockDocumentTemplatePart({
	blockId,
	documentTemplatePartId,
}: {
	blockId: string;
	documentTemplatePartId: string;
}) {
	await axios.delete(
		`${env.apiUrl}/blocks/${blockId}/document-template-parts/${documentTemplatePartId}`,
	);
}

export async function deleteBlock(blockId: string) {
	const response = await axios.delete<{ updatedBlocks: Block[] }>(
		`${env.apiUrl}/blocks/${blockId}`,
	);

	return response.data;
}

export async function addBlockParent({
	blockId,
	parentIds,
}: {
	blockId: string;
	parentIds: string[];
}) {
	await axios.post(`${env.apiUrl}/blocks/parents`, { blockId, parentIds });
}

export async function deleteBlockParent({
	blockId,
	parentIds,
}: {
	blockId: string;
	parentIds: string[];
}) {
	await axios.delete(`${env.apiUrl}/blocks/parents`, { data: { blockId, parentIds } });
}

export const getBlockChildrens = queryOptions({
	async queryFn({ queryKey }) {
		const [, { blockParentId }] = queryKey;

		const response = await axios.get<Block[]>(`${env.apiUrl}/blocks/${blockParentId}/children`);

		return response.data;
	},
	queryKey: "getBlockChildrens",
});

export async function updateBlockOrder({ blockIds }: { blockIds: string[] }) {
	const response = await axios.patch<Block[]>(`${env.apiUrl}/blocks/reorder`, {
		blockIds,
	});

	return response.data;
}

export async function setBlockDocumentTemplate({
	blockId,
	...body
}: SetBlockDocumentTemplateBody & { blockId: string }) {
	const response = await axios.put<BlockDocumentTemplatePart[]>(
		`${env.apiUrl}/blocks/${blockId}/document-template`,
		body,
	);

	return response.data;
}

export async function removeBlockDocumentTemplate(blockId: string) {
	await axios.delete(`${env.apiUrl}/blocks/${blockId}/document-template`);
}
